import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import type { PanelState, PanelHookReturn } from './types';

/**
 * Hook for managing panel state via URL search params
 *
 * @example
 * ```tsx
 * // URL-based state (default)
 * const { isOpen, type, id, openPanel, closePanel } = usePanelState();
 *
 * // Open a panel
 * openPanel('user', '123', { tab: 'details' });
 * // URL becomes: ?panel=user&id=123&tab=details
 *
 * // Close panel
 * closePanel();
 * // URL becomes: (empty)
 * ```
 */
export function usePanelState(): PanelHookReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const panelState = useMemo<PanelState>(() => {
    const type = searchParams.get('panel');
    const id = searchParams.get('id');

    return {
      isOpen: !!type && !!id,
      type,
      id,
    };
  }, [searchParams]);

  const openPanel = useCallback(
    (type: string, id: string, additionalParams?: Record<string, string>) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('panel', type);
        next.set('id', id);
        if (additionalParams) {
          for (const [key, value] of Object.entries(additionalParams)) {
            next.set(key, value);
          }
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const closePanel = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('panel');
      next.delete('id');
      return next;
    });
  }, [setSearchParams]);

  return {
    ...panelState,
    openPanel,
    closePanel,
  };
}
